import { sendMessage } from '@composables/useWindowEvents.js'
import lodashThrottle from 'lodash/throttle'
import { getCurrentInstance } from 'vue'
import cloneDeep from 'lodash/cloneDeep'
import isURL from 'validator/lib/isURL'
import { customAlphabet } from 'nanoid'
import readingTime from 'reading-time'
import equal from 'lodash/isEqual'
import filesize from 'filesize'
import get from 'lodash/get'
import qs from 'qs'
import {
  ONLINE_USER_TIMEOUT_SECONDS,
  PHP_UNIXTIME_LEN,
  IMAGE_CROP_SIZES,
  LOCALE_LANGUAGE,
} from '@config'

export const MS_YEAR = 365 * 24 * 60 * 60 * 1000
export const MS_DAY = 24 * 60 * 60 * 1000
export const MS_HOUR = 60 * 60 * 1000
export const MS_MINUTE = 60 * 1000
export const MS_SECOND = 1000

export const HEADER_HEIGHT = 80

export const isClient = typeof window !== 'undefined'
export const defaultDocument = isClient ? window.document : undefined

/* istanbul ignore next */
export function isEqual(value, other) {
  return equal(value, other)
}

export function genRanId() {
  const nanoid = customAlphabet(
    '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz',
    21
  )

  return nanoid()
}

export function genHtmlId() {
  const instance = getCurrentInstance()
  return `input${instance?.uid || genRanId()}`
}

export function stringProper(text) {
  return text
    .toString()
    .split(' ')
    .map((word) => word[0].toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export function callOrReturn(input) {
  if (!input) {
    return
  } else if (isFunction(input)) {
    return input()
  } else {
    return input
  }
}

export function isFunction(val) {
  return toString.call(val) === '[object Function]'
}

export function isArray(val) {
  return toString.call(val) === '[object Array]'
}

export function isDate(val) {
  return toString.call(val) === '[object Date]'
}

export function isObj(val) {
  return toString.call(val) === '[object Object]'
}

export function isString(val) {
  return toString.call(val) === '[object String]'
}

export function isRegex(val) {
  return toString.call(val) === '[object RegExp]'
}

export function isFile(val) {
  return toString.call(val) === '[object File]'
}

export function isBoolean(val) {
  return (
    toString.call(val) === '[object Boolean]' ||
    val === 1 ||
    val === 0 ||
    val === '0' ||
    val === '1'
  )
}

export function isNumber(val) {
  return typeof val === 'number'
}

export function isObjEmpty(obj) {
  return isObj(obj) && Object.keys(obj).length === 0
}

export function clone(data) {
  return cloneDeep(data)
}

export function removeProtocol(url) {
  if (!url) return

  if (!isURL(url, { require_protocol: true })) return url

  return url.replace(/^.+?:\/\//, '')
}

export function pad(input, count, char = '0') {
  if (input.toString().length >= count) return input.toString()

  const start = new Array(count).fill(char).join('')
  return (start + input).slice(-count)
}

export function queryString(query) {
  if (!isObj(query) || !query) {
    return ''
  }
  return `?${qs.stringify(query)}`
}

export function unixTime(date) {
  return isDate(date) ? parseInt((date.getTime() / 1000).toFixed(0)) : null
}

export function timeSince(unixTime, { short = true } = {}) {
  if (!unixTime) return ''
  const current = parseInt(Date.now().toFixed(0))
  const past = secsToMs(unixTime)
  if (past > current) {
    return ''
  }

  const diff = current - past

  if (diff >= MS_YEAR) {
    const years = Math.floor(diff / MS_YEAR)
    if (short) {
      return `${years}y`
    } else {
      return years === 1 ? `${years} year` : `${years} years`
    }
  } else if (diff >= MS_DAY) {
    const days = Math.floor(diff / MS_DAY)
    if (short) {
      return `${days}d`
    } else {
      return days === 1 ? `${days} day` : `${days} days`
    }
  } else if (diff >= MS_HOUR) {
    const hours = Math.floor(diff / MS_HOUR)
    if (short) {
      return `${hours}h`
    } else {
      return hours === 1 ? `${hours} hour` : `${hours} hours`
    }
  } else if (diff >= MS_MINUTE) {
    const mins = Math.floor(diff / MS_MINUTE)
    if (short) {
      return `${mins}m`
    } else {
      return mins === 1 ? `${mins} minute` : `${mins} minutes`
    }
  } else if (diff >= 0) {
    const secs = Math.floor(diff / MS_SECOND)
    if (short) {
      return `${secs}s`
    } else {
      return secs === 1 ? `${secs} second` : `${secs} seconds`
    }
  }
}

export function timeRemaining(unixTime) {
  if (!unixTime) return ''
  const current = msToSecs(Date.now())

  let timeRemaining = Math.max(unixTime - current, 0)

  const days = Math.floor(timeRemaining / 86400)
  timeRemaining -= days * 86400

  const hours = Math.floor(timeRemaining / 3600)
  timeRemaining -= hours * 3600

  const mins = Math.floor(timeRemaining / 60)
  timeRemaining -= mins * 60

  return `${days > 999 ? '+999' : days}:${pad(hours, 2)}:${pad(mins, 2)}:${pad(
    timeRemaining,
    2
  )}`
}

export function isOnlineUser(unixTime) {
  const current = parseInt(Date.now().toFixed(0))
  const lastOnline = secsToMs(unixTime)

  return lastOnline > current - secsToMs(ONLINE_USER_TIMEOUT_SECONDS)
}

export function humanDateFormat(timestamp, dateTime = false) {
  if (!timestamp) {
    return ''
  }
  if (!isNumber(timestamp)) {
    return ''
  }

  let dateObject

  if (getNumberLength(timestamp) <= PHP_UNIXTIME_LEN) {
    dateObject = new Date(secsToMs(timestamp))
  } else {
    dateObject = new Date(timestamp)
  }

  return dateTime
    ? dateObject.toLocaleTimeString(undefined, {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      })
    : dateObject.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
}

export function humanNumberFormat(number) {
  if (!isNumber(number)) {
    return 0
  }

  return number.toLocaleString(LOCALE_LANGUAGE)
}

export function humanFilesize(bits, options) {
  return filesize(bits, options)
}

export function getNumberLength(number) {
  return number.toString().length
}

export function proper(text) {
  if (!text || !isString(text)) {
    return
  }

  return text
    .split(' ')
    .map((word) => {
      const firstLetter = word.slice(0, 1)
      const rest = word.slice(1)
      return `${firstLetter.toUpperCase()}${rest.toLowerCase()}`
    })
    .join(' ')
}

/* istanbul ignore next */
export function throttle(fn, duration) {
  return lodashThrottle(fn, duration, {
    leading: false,
  })
}

export function objectGet(object, key) {
  return get(object, key)
}

export function secsToMs(secs) {
  return secs * MS_SECOND
}

export function msToSecs(ms) {
  return parseInt((ms / MS_SECOND).toFixed(0))
}

export function youtubeParser(url) {
  var regExp =
    /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/
  var match = url.match(regExp)
  return match && match[7].length == 11 ? match[7] : false
}

export function numberFormatter(num, places = 0) {
  const lookup = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'K' },
    { value: 1e6, symbol: 'M' },
    { value: 1e9, symbol: 'B' },
    { value: 1e12, symbol: 'T' },
    { value: 1e15, symbol: 'P' },
    { value: 1e18, symbol: 'E' },
  ]
  const rx = /\.0+$|(\.[0-9]*[1-9])0+$/
  const item = lookup
    .slice()
    .reverse()
    .find((item) => num >= item.value)
  return item
    ? (num / item.value).toFixed(places).replace(rx, '$1') + item.symbol
    : '0'
}

/* istanbul ignore next */
export function asyncSleep(ms) {
  return new Promise((resolve) => {
    setTimeout(resolve, ms)
  })
}

// Image crop using url format crop_320x180
export function getCropImageUrl({ originalUrl, size, type = 'crop' }) {
  // throws type error if not a valid url
  new URL(originalUrl)
  if (!IMAGE_CROP_SIZES.includes(size)) {
    throw new Error('Crop size not allowed.')
  }
  return originalUrl.replace(
    /(crop|thumb|image)_([0-9]+x[0-9]+)/,
    `${type}_${size}`
  )
}

export function toCamelCase(str, separator = '_') {
  return str
    .split(separator)
    .map((word, idx) =>
      idx === 0 ? word : word[0].toUpperCase() + word.slice(1).toLowerCase()
    )
    .join('')
}

export function areBitsSet(value, mask) {
  return (value & mask) >= 1
}

export function setBits(value, mask) {
  return value | mask
}

export function unsetBits(value, mask) {
  return value & ~mask
}

export function generateSortQuery(direction, sortKey) {
  return direction === 'desc' ? `-${sortKey}` : sortKey
}

export function apiErrorsToArray(error) {
  if (isObj(error) && !isObjEmpty(error)) {
    let arr = []
    Object.values(error).forEach((v) => {
      if (isObj(v) && !isObjEmpty(v)) {
        Object.values(v).forEach((x) => {
          if (isString(x)) arr.push(x)
        })
      } else {
        arr.push(v)
      }
    })
    return arr
  } else if (isString(error)) {
    return [error]
  } else {
    return null
  }
}

export function getProps(item, card) {
  const itemProps = {}
  card.footerProps &&
    isArray(card.footerProps) &&
    card.footerProps.forEach((element) => {
      itemProps[toCamelCase(element)] = item[element]
    })

  return itemProps
}

export function isNavigationInSameBrowseView(to, from) {
  return to.meta?.browse && to.name === from.name
}

export function isSameRouteChild(to, from) {
  return to.meta?.children?.includes(from.name) || from.fullPath === to.fullPath
}

export function isIframe() {
  try {
    return window.self !== window.top
  } catch (e) {
    return true
  }
}

export function handleScroll(to, from) {
  // handles useBrowse push
  if (!isNavigationInSameBrowseView(to, from) || to.hash) {
    sendMessage({ modioScroll: 0 })
  }
}

export function getQueryFromPath(url) {
  return url.includes('?') ? qs.parse(url.split('?').slice(-1)[0]) : {}
}

const cleanElem = document.createElement('div') // here so it's not recreated every time the function is called
export function cleanHTMLEntities(text) {
  cleanElem.innerHTML = text
  return cleanElem.textContent
}

export function scrollToElement(idOrEl) {
  let target
  if (isString(idOrEl)) {
    target = defaultDocument.getElementById(idOrEl)
  } else {
    target = idOrEl
  }

  if (target) {
    target.scrollIntoView()
  }
}

export function getReadingTime(string) {
  return readingTime(string)
}
